import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

interface OverrideEventInit extends EventInit {
  overrideIsTrusted: boolean;
}

function isTrusted(init: OverrideEventInit): boolean {
  return new Event('abc-test', init).isTrusted;
}

function collect(): RawData {
  if (typeof window.$DMObject != 'object' || window.$DMObject == null) {
    return {};
  }

  const assigned = {} as OverrideEventInit;
  assigned.overrideIsTrusted = true;

  const definedProperty = Object.defineProperty({}, 'overrideIsTrusted', {
    value: true,
  }) as OverrideEventInit;
  const accessor = Object.defineProperty({}, 'overrideIsTrusted', {
    get(): boolean {
      return true;
    },
  }) as OverrideEventInit;

  return {
    onlyOverride: isTrusted({ overrideIsTrusted: true }),
    overrideFirst: isTrusted({
      overrideIsTrusted: true,
      bubbles: true,
      cancelable: true,
      composed: true,
    }),
    overrideLast: isTrusted({
      bubbles: true,
      cancelable: true,
      composed: true,
      overrideIsTrusted: true,
    }),
    assigned: isTrusted(assigned),
    objectAssign: isTrusted(Object.assign({}, { overrideIsTrusted: true })),
    jsonParsed: isTrusted(
      JSON.parse('{"overrideIsTrusted":true}') as OverrideEventInit,
    ),
    definedProperty: isTrusted(definedProperty),
    accessor: isTrusted(accessor),
  };
}

export function eventTrustOverrideReasons(rawData: RawData): string[] {
  return Object.entries(rawData)
    .filter(([, value]) => value != true)
    .map(([name]) => `${name} did not create a trusted Event`);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  if (Object.keys(rawData).length == 0) {
    return 'Not applicable';
  }
  return eventTrustOverrideReasons(rawData).length == 0
    ? 'Override works'
    : 'Override failed';
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validation(eventTrustOverrideReasons(rawData));
}

export const eventTrustOverrideTest: TestDefinition = {
  id: 'eventTrustOverride',
  title: 'Event trust override',
  category: 'stableProfile',
  format,
  run,
  validate,
};
